import moment from "moment";

export function dateToString(date, dateOnly = false, pretty = false) {
  if (!date) {
    return "";
  }

  if (dateOnly) {
    return moment(date).format(pretty ? "DD.MM.YYYY" : "YYYY-MM-DD");
  }
  return moment(date).format(pretty ? "DD.MM.YYYY HH:mm" : "YYYY-MM-DDTHH:mm:ssZ")
}

export function dateToTime(date) {
  if (!date) {
    return "";
  }

  return moment(date).format("HH:mm");
}

/**
 * @param Date date string (eg, '2022-10-15')
 * @returns {String} localized date string (eg. 'Oct 14, 2022')
 */
export function dateToLocalizedFormat(date) {
  return moment(date).format("ll");
}

/**
 * @param date HH:mm:ss ISO8601 time
 * @returns {String|null} HH:MM time or null if input string is null
 */
export function parseISOTime(time) {
  if (time == null) {
    return null;
  }
  const components = time.split(":");
  return `${components[0]}:${components[1]}`;
}

/**
 * @param startTime HH:mm time
 * @param endTime HH:mm time
 * @returns {boolean}
 */
export function isTimeGreaterThan(startTime, endTime) {
  return moment(startTime, 'HH:mm').diff(moment(endTime, 'HH:mm')) > 0;
}

/**
 * @param date String containing date and/or time
 * @param pretty Flag which represents if date is is pretty format ('D.M.YYYY HH:mm', 'D.M.YYYY') or in ISO format: ('YYYY-MM-DDTHH:mm:ssZ', 'YYYY-MM-DD')
 * @param hasTime If true return null if string does not have time included
 * @returns {Date|null|*} Date object or null if string is invalid date
 */
export function stringToDate(date, pretty = false, hasTime = false) {
  if (typeof date !== "string") {
    return date;
  }
  let value = moment(date, pretty ? "D.M.YYYY HH:mm" : "YYYY-MM-DDTHH:mm:ssZ", true);
  if (!value.isValid() && !hasTime) {
    value = moment(date, pretty ? "D.M.YYYY" : "YYYY-MM-DD", true);
    if (!value.isValid()) {
      value = moment(date, pretty ? "DDMMYYYY" : "YYYYMMDD", true);
    }
  }
  return value.isValid() ? value.toDate() : null;
}

export function fromNow(date) {
  if (!date) return "";
  date = stringToDate(date);
  return moment(date, undefined, "sl").fromNow();
}

export function toNow(date, skipPrefix = false) {
  date = stringToDate(date);
  return moment(date, undefined, "sl").toNow(skipPrefix);
}

export function duration(start, end, t = undefined) {
  start = stringToDate(start);
  end = stringToDate(end);
  const diff = moment(end).diff(moment(start));
  const duration = moment.duration(diff);
  if (t) {
    //if we have translation object we return detailed string
    const years = duration.years();
    const months = duration.months();
    const days = duration.days();
    if (years > 0) {
      return t("Years", {count: years}) + " " + t("Months", {count: months}) + " " + t("Days", {count: days});
    } else if (months > 0) {
      return t("Months", {count: months}) + " " + t("Days", {count: days});
    }
    return t("Days", {count: days});
  } else {
    return duration.locale("sl").humanize();
  }
}

export function durationInDays(start, end) {
  start = stringToDate(start);
  end = stringToDate(end);
  const diff = moment(end).diff(moment(start));
  const duration = moment.duration(diff);
  return duration.asDays();
}

/**
 * @returns String in format HH:mm
 */
export function toDuration(minutes){
  return Math.floor(minutes/60).toString().padStart(2, "0") + ":" +
    (minutes % 60).toString().padStart(2, "0");
}

/**
 * Remove seconds from time in string format
 * @param time String in format HH:mm:ss
 * @returns String in format HH:mm
 */
export function trimSeconds(time){
  return time.split(":", 2).join(":");
}

/**
 * Compares two dates without comparing the timestamps
 * @param date1 Date object
 * @param date2 Date object
 * @returns {boolean} True if date1 is smaller than date2 else False
 */
export function isDateSmallerThan(date1, date2) {
  date1 = new Date(date1.toDateString())
  date2 = new Date(date2.toDateString())
  return date1 < date2;
}

/**
 * Calculates number of days between two dates
 * @param date1 Date object
 * @param date2 Date object
 * @returns {number} difference between dates (in days)
 */
export function daysBetweenDates(date1, date2) {
  return Math.ceil((date1 - date2) / (1000 * 60 * 60 * 24));
}

/**
 * Checks if time is between start and end time
 * @param time
 * @param startTime
 * @param endTime
 * @returns {boolean} True if time is between start and end time, false otherwise
 */
export function isTimeBetween(time, startTime, endTime){
  if(startTime === endTime){
    return true;
  }
  if(isTimeGreaterThan(endTime, startTime)){
    return !(isTimeGreaterThan(startTime, time) ||
      isTimeGreaterThan(time, endTime));
  }
  return !(isTimeGreaterThan(startTime, time) &&
    isTimeGreaterThan(time, endTime));
}

function appendYear(date) {
  date.setFullYear(date.getFullYear() + 1);
  return date;
}

export function addDays(date, days) {
  return new Date(date.getTime() + 86400000  * days);
}

export const today = new Date();
export const todayDateString = moment(new Date()).format("YYYY-MM-DD");
export const yesterday = new Date(Date.now() - 86400000);
export const currentYear = new Date().getFullYear();
export const nextYear = appendYear(new Date());
